import { useEffect, useState } from "react";
import { Outlet, useNavigate, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { useI18n } from "@/contexts/I18nContext";
import Copilot from "@/components/Copilot";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ScenarioSelector from "@/components/ScenarioSelector";
import {
  LayoutDashboard, Network, ShieldAlert, Workflow,
  ShieldCheck, ChevronDown, LogOut, Sparkles, Search, Bell, Activity, BarChart3, Cpu, Brain,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", key: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/network", key: "nav.network", icon: Network, testid: "nav-network" },
  { to: "/soc", key: "nav.soc", icon: ShieldAlert, testid: "nav-soc" },
  { to: "/analytics", key: "nav.analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/telemetry", key: "nav.telemetry", icon: Cpu, testid: "nav-telemetry" },
  { to: "/agents", key: "nav.agents", icon: Brain, testid: "nav-agents" },
];

export default function AppLayout() {
  const { user, loading, logout } = useAuth();
  const { tenant, tenants, switchTenant } = useTenant();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [tenantOpen, setTenantOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotPrompt, setCopilotPrompt] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/?showAccess=1", { replace: true });
  }, [user, loading, navigate]);

  // Cross-page Copilot opener — any component can fire window.dispatchEvent(
  //   new CustomEvent('evos:open-copilot', { detail: { prompt: '...' } }))
  useEffect(() => {
    const handler = (e) => {
      setCopilotPrompt(e.detail?.prompt || "");
      setCopilotOpen(true);
    };
    window.addEventListener("evos:open-copilot", handler);
    return () => window.removeEventListener("evos:open-copilot", handler);
  }, []);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center text-slate-500 font-mono text-xs">
        <span className="w-2 h-2 bg-[#3B82F6] evos-blink mr-3" /> Loading EVOS Cockpit...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100">
      {/* SIDEBAR */}
      <aside className="w-64 border-r border-slate-800 bg-[#020617] h-screen fixed left-0 top-0 z-40 flex flex-col">
        <div className="h-16 border-b border-slate-800 flex items-center gap-3 px-5">
          <img
            src="/evos-logo.png"
            alt="EVOS"
            data-testid="sidebar-logo"
            className="h-9 w-9 rounded-sm object-cover"
          />
          <div>
            <div className="font-display font-black text-lg tracking-tight">EVOS</div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-mono">IT Cockpit</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-600 font-mono px-3 mb-3">
            {t("nav.section_ops")}
          </div>
          {NAV.map(({ to, key, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive
                    ? "bg-[#1E3A8A]/20 text-white border-l-2 border-[#3B82F6]"
                    : "text-slate-400 hover:text-slate-100 hover:bg-[#0F172A] border-l-2 border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{t(key)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-800 p-4 space-y-3">
          <button
            data-testid="open-copilot-btn"
            onClick={() => setCopilotOpen(true)}
            className="w-full flex items-center gap-2 bg-[#172554] hover:bg-[#1E3A8A] transition-colors border border-[#1E3A8A]/60 text-slate-100 px-3 py-2.5 rounded-sm text-sm"
          >
            <Sparkles className="w-4 h-4 text-[#60A5FA]" />
            <span className="font-semibold">EVOS Copilot</span>
            <span className="ml-auto text-[10px] font-mono text-slate-400">AI</span>
          </button>

          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-sm border border-slate-800" />
            ) : (
              <div className="w-8 h-8 rounded-sm bg-[#1E3A8A] flex items-center justify-center text-xs font-bold">
                {(user.name || user.email || "U").slice(0,1).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{user.name || user.email}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span
                  className={`text-[9px] font-mono font-bold px-1.5 py-0.5 uppercase tracking-wider border ${
                    user.rbac_role === "admin"
                      ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                      : user.rbac_role === "ro-viewer"
                      ? "border-slate-600 text-slate-400 bg-slate-700/20"
                      : "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                  }`}
                  data-testid="rbac-role-badge"
                >
                  {user.rbac_role || "ops"}
                </span>
                <span className="text-[10px] text-slate-500 truncate font-mono">{user.role || "IT Manager"}</span>
              </div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="text-slate-500 hover:text-red-400">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* HEADER */}
      <header className="h-16 border-b border-slate-800 bg-[#020617]/90 backdrop-blur-md sticky top-0 z-30 flex items-center px-6 ml-64 justify-between gap-4">
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Language switcher — top-left of the page */}
          <LanguageSwitcher />
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono hidden lg:block">
            {location.pathname.replace("/", "").toUpperCase() || "DASHBOARD"}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-1 justify-center">
          {/* Tenant Switcher */}
          <div className="relative">
            <button
              data-testid="tenant-switcher-btn"
              onClick={() => setTenantOpen((v) => !v)}
              className="flex items-center gap-3 px-3 py-2 border border-slate-800 hover:border-[#1E3A8A] bg-[#0F172A] rounded-sm transition-colors min-w-[280px]"
            >
              <div className="w-7 h-7 bg-[#1E3A8A]/30 border border-[#1E3A8A]/40 flex items-center justify-center text-[10px] font-mono font-bold text-[#60A5FA]">
                {tenant?.short?.slice(0,3) || "—"}
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="text-xs font-semibold truncate">{tenant?.name || "Select Tenant"}</div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-mono">
                  {tenant?.sector}
                </div>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-500" />
            </button>

            {tenantOpen && (
              <div className="absolute top-full mt-1 left-0 w-[380px] border border-slate-800 bg-[#0F172A] rounded-sm shadow-2xl z-50" data-testid="tenant-dropdown">
                {tenants.map((ten) => (
                  <button
                    key={ten.id}
                    data-testid={`tenant-option-${ten.id}`}
                    onClick={() => { switchTenant(ten.id); setTenantOpen(false); }}
                    className={`w-full text-left p-3 border-b border-slate-800/60 hover:bg-[#1E293B] transition-colors flex items-center gap-3 ${
                      tenant?.id === ten.id ? "bg-[#1E3A8A]/10" : ""
                    }`}
                  >
                    <div className="w-9 h-9 bg-[#1E3A8A]/20 border border-[#1E3A8A]/40 flex items-center justify-center text-[10px] font-mono font-bold text-[#60A5FA]">
                      {ten.short.slice(0,3)}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{ten.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">
                        {ten.sector} • {ten.sites} sites • {ten.users.toLocaleString()} users
                      </div>
                    </div>
                    {tenant?.id === ten.id && <span className="w-2 h-2 bg-emerald-400 rounded-full" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Scenario selector — next to tenant in the upper center */}
          <ScenarioSelector />
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="hidden md:flex items-center gap-2 border border-slate-800 bg-[#0F172A] px-3 py-1.5 text-xs font-mono">
            <Activity className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-slate-400">{t("header.system")}</span>
            <span className="text-emerald-400">● {t("header.operational").toUpperCase()}</span>
          </div>
          <button className="border border-slate-800 hover:border-[#1E3A8A] bg-[#0F172A] p-2 rounded-sm transition-colors">
            <Search className="w-4 h-4 text-slate-400" />
          </button>
          <button className="relative border border-slate-800 hover:border-[#1E3A8A] bg-[#0F172A] p-2 rounded-sm transition-colors">
            <Bell className="w-4 h-4 text-slate-400" />
            <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-red-500 rounded-full evos-pulse" />
          </button>
        </div>
      </header>

      {/* MAIN */}
      <main className="ml-64 min-h-[calc(100vh-4rem)] p-6">
        <Outlet />
      </main>

      <Copilot open={copilotOpen} onClose={() => { setCopilotOpen(false); setCopilotPrompt(""); }} initialPrompt={copilotPrompt} />
    </div>
  );
}
